import axios from 'axios'
import type { ContentScope } from '@/types/contentScope'
import useLocalStorage from './useLocalStorage'

import { transformRuleRecordToApiArray, transformApiArrayToRuleRecord } from '@/utils/audienceRules'

import type { Audience, AudienceRule } from '@/types/types'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { AudienceRuleRecord, ApiAudienceRule } from '@/types/audience'

export const getAudienceColor = (type: string) => {
  switch (type) {
    case 'typology':
      return 'violet'
    case 'dynamic':
      return 'indigo'
    case 'static':
      return 'orange'
  }
}

export default function useAudience() {
  const { user } = useLocalStorage()
  const notification = useNotification()
  const router = useRouter()
  const { t } = useI18n()

  const merchantId = user.value.merchant.id

  const relativeOperators = ref([
    {
      id: 1,
      value: 'absolute',
      text: t('pages.audience.operators.date.absolute'),
      color: 'text-foreground-secondary',
      disabled: true
    },
    {
      id: 2,
      value: 'between',
      text: t('pages.audience.operators.date.between')
    },
    {
      id: 3,
      value: 'gt',
      text: t('pages.audience.operators.date.gt')
    },
    {
      id: 4,
      value: 'lt',
      text: t('pages.audience.operators.date.lt')
    },
    {
      id: 5,
      value: 'eq',
      text: t('pages.audience.operators.date.eq')
    },
    {
      id: 6,
      value: 'relative',
      text: t('pages.audience.operators.date.relative'),
      color: 'text-foreground-secondary',
      disabled: true
    },
    {
      id: 7,
      value: 'lt',
      text: t('pages.audience.operators.date.relatives.lt')
    },
    {
      id: 8,
      value: 'gt',
      text: t('pages.audience.operators.date.relatives.gt')
    },
    {
      id: 9,
      value: 'lte',
      text: t('pages.audience.operators.date.relatives.lte')
    },
    {
      id: 10,
      value: 'gte',
      text: t('pages.audience.operators.date.relatives.gte')
    },
    {
      id: 11,
      value: 'eq',
      text: t('pages.audience.operators.date.relatives.eq')
    }
  ])

  const basicDateOperators = computed(() => relativeOperators.value.slice(1, 5))

  const isRuleValueValid = (value) => {
    if (!value) {
      return false
    }
    const isValueArray = Array.isArray(value)
    if (isValueArray && value.length < 1) {
      return false
    }
    if (isValueArray && value.length) {
      const isValueDefinedAndValid = value[0] !== '' && value[0] !== undefined
      const isValidArray = isValueArray && value.length >= 1 && isValueDefinedAndValid
      return isValidArray
    }

    if (typeof value === 'number') {
      return value >= 0
    }
    const isValueDefinedAndValid = value !== '' && value !== undefined
    return isValueDefinedAndValid
  }

  function determineRuleType(value: string | number | string[]): string {
    if (typeof value === 'number') return 'relative'
    if (Array.isArray(value) && value.length > 0) {
      const dateString = value[0]
      const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/
      return typeof dateString === 'string' && dateRegex.test(dateString) ? 'absolute' : 'relative'
    }
    return 'absolute'
  }

  const fetchAudienceById = async (audienceId: number) => {
    try {
      const { data } = await axios.get(`/merchants/${merchantId}/segments/${audienceId}`)
      return data.data
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      if (error?.response?.status === 404) {
        notification.create('error', {
          text: t('pages.customer.audienceDetailPage.loading.error.notFound'),
          timeout: 3000
        })
        router.push({ name: 'AudienceList' })
      }
      throw new Error(error)
    }
  }

  const fetchAudienceByTemplate = async (templateUsed: string) => {
    try {
      const { data } = await axios.get<{ data: Audience[] }>(
        `/merchants/${merchantId}/segments/by-template/${templateUsed}`
      )
      return data.data
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      if (error?.response?.status === 404) {
        return null
      }
      throw new Error(error)
    }
  }

  const fetchMerchantAudienceMass = async (
    audienceId: number,
    pagination = { pageSize: 1, page: 1 },
    sort = { sortBy: null, sortOrder: null },
    cancelToken = null
  ) => {
    try {
      const params = {
        pageSize: pagination.pageSize,
        page: pagination.page,
        sortBy: sort.sortBy,
        sortOrder: sort?.sortOrder
      }
      const { data } = await axios.get(`/merchants/${merchantId}/segments/${audienceId}/customers`, {
        params,
        cancelToken
      })
      return data
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      throw new Error(error)
    }
  }

  const fetchMerchantAudienceCustomersCount = async (audienceId: number) => {
    try {
      const { data } = await axios.get(`/merchants/${merchantId}/segments/${audienceId}/customers/count`)
      return data.data.total
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      throw new Error(error)
    }
  }

  const fetchMerchantAudiences = async ({
    pagination = { pageSize: 15, page: 1 },
    sort = { sortBy: null, sortOrder: null },
    search = null,
    type = null,
    storeIds = null,
    scopeParams = {},
    cancelToken = null
  } = {}) => {
    try {
      const params = {
        pageSize: pagination.pageSize,
        page: pagination.page,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        search,
        type,
        storeIds,
        ...scopeParams
      }

      const response = await axios.get(`/merchants/${merchantId}/segments`, { params, cancelToken })
      if (response.status === 404) {
        return []
      }
      return response.data
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      throw new Error(error)
    }
  }

  const isAudienceRuleWithParameters = (rule: AudienceRule) => {
    return (
      typeof rule === 'object' &&
      rule !== null &&
      Object.prototype.hasOwnProperty.call(rule, 'parameters') &&
      typeof rule['parameters'] === 'object'
    )
  }

  const fetchAudienceRules = async (audienceId: number) => {
    try {
      const { data } = await axios.get<{ data: ApiAudienceRule[] }>(
        `/merchants/${merchantId}/segments/${audienceId}/rules`
      )

      const apiRules: ApiAudienceRule[] = data.data
      return transformApiArrayToRuleRecord(apiRules)
    } catch (error) {
      throw new Error(error)
    }
  }

  const bulkUpdateAudienceRules = async (audienceId: number, rules: ApiAudienceRule[]) => {
    try {
      const { data } = await axios.put(`/merchants/${merchantId}/segments/${audienceId}/rules`, {
        rules
      })
      return data.data
    } catch (error) {
      throw new Error(error)
    }
  }

  const fetchEstimateAudience = async (
    rules: AudienceRule[],
    storeIds: number[],
    token: ReturnType<typeof axios.CancelToken.source>['token'] | undefined
  ) => {
    try {
      const { data } = await axios.post(
        `/merchants/${merchantId}/segments/rules/audience`,
        {
          rules,
          ...(storeIds?.length ? { storeIds } : {})
        },
        { cancelToken: token }
      )
      return data.data
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      throw new Error(error)
    }
  }

  const editAudienceName = async (segment: Audience) => {
    try {
      const { data } = await axios.put(`merchants/${merchantId}/segments/${segment.id}`, {
        name: segment.name
      })
      notification.create('success', {
        text: t('pages.customer.audienceDetailPage.edit.notificationSuccess'),
        timeout: 3000
      })
      return data.data
    } catch (error) {
      notification.create('error', {
        text: t('pages.customer.audienceDetailPage.edit.notificationError'),
        timeout: 3000
      })
      throw new Error(error)
    }
  }

  const editAudienceScope = async (segment: Audience, scope: ContentScope): Promise<Audience> => {
    const { data } = await axios.put(`merchants/${merchantId}/segments/${segment.id}`, {
      name: segment.name,
      scope
    })
    return data.data
  }

  const editAudienceStores = async (segment: Audience, storeIds: number[]) => {
    try {
      const { data } = await axios.put(`merchants/${merchantId}/segments/${segment.id}`, {
        name: segment.name,
        store_ids: storeIds
      })
      notification.create('success', {
        text: t('pages.customer.audienceDetailPage.editStores.notificationSuccess'),
        timeout: 3000
      })
      return data.data
    } catch (error) {
      notification.create('error', {
        text: t('pages.customer.audienceDetailPage.editStores.notificationError'),
        timeout: 3000
      })
      throw new Error(error)
    }
  }

  const handleSubmitSegmentRules = async (
    audienceId: number,
    rules: Record<string, AudienceRule>,
    sendNotification = true
  ) => {
    if (!audienceId) {
      return
    }

    try {
      const data = formatAudienceRulesRecordToArray(rules)
      await bulkUpdateAudienceRules(audienceId, data)

      if (sendNotification) {
        notification.create('success', {
          text: t('pages.audience.audienceCreatePage.notifications.rules.success')
        })
      }
    } catch (error) {
      notification.create('error', {
        text: t('pages.audience.audienceCreatePage.notifications.rules.error')
      })
      throw new Error(`Error while updating audience rules ${error}`)
    }
  }

  const formatAudienceRulesRecordToArray = (rules: Record<string, AudienceRule> | AudienceRuleRecord) => {
    if (!rules) {
      return []
    }

    const validRules = Object.entries(rules).filter(([, rule]) => {
      return isRuleValueValid(rule.value)
    })

    const isAlreadyKebabCase = (attr: string) => !attr.includes('_')

    const componentRules: AudienceRuleRecord = {}
    validRules.forEach(([attribute, rule]) => {
      const kebabAttribute = isAlreadyKebabCase(attribute) ? attribute : attribute.replaceAll(/_/g, '-')
      componentRules[kebabAttribute] = {
        attribute: kebabAttribute,
        operator: rule.operator,
        value: rule.value,
        ...(rule.parameters && { parameters: rule.parameters })
      }
    })

    return transformRuleRecordToApiArray(componentRules)
  }

  const createAudience = async ({
    audience,
    rules,
    sendNotification = true
  }: {
    audience: Audience
    rules?: Record<string, AudienceRule>
    sendNotification?: boolean
  }): Promise<number | undefined> => {
    if (!audience?.name || !audience?.type) {
      return undefined
    }

    try {
      const formattedRules = formatAudienceRulesRecordToArray(rules)
      const params = {
        name: audience.name,
        type: audience.type,
        template_used: audience.template_used,
        store_ids: audience.store_ids,
        rules: formattedRules,
        ...(audience.scope && { scope: audience.scope })
      }

      const { data } = await axios.post(`/merchants/${merchantId}/segments/`, params)

      if (!data || !data.data) {
        throw new Error('Invalid response from server.')
      }

      if (formattedRules.length > 0) {
        await handleSubmitSegmentRules(data.data.id, rules, sendNotification)
      }

      if (sendNotification) {
        notification.create('success', {
          text: t('pages.customer.audiences.create.notificationSuccess', { audienceName: audience.name }),
          timeout: 3000
        })
      }

      return data.data.id
    } catch (error) {
      let errorMessage = 'An error occurred while creating the audience.'

      if (error?.response?.data) {
        const responseData = error.response.data
        if (responseData.errors?.name && responseData.errors?.name?.length > 0) {
          errorMessage = t('pages.customer.audiences.create.errorMessages.name')
        } else if (responseData.errors?.rules?.length > 0) {
          errorMessage = t('pages.customer.audiences.create.errors.emptyRules')
        } else {
          errorMessage = responseData?.message || errorMessage
        }
      }

      if (sendNotification) {
        notification.create('error', {
          text: t('pages.customer.audiences.create.notificationError', { errorMessage }),
          timeout: 3000
        })
      }

      throw { message: errorMessage }
    }
  }

  const deleteAudience = async (audience) => {
    await axios.delete(`/merchants/${user.value.merchant.id}/segments/${audience.id}`)
    notification.create('success', {
      text: t('pages.customer.audiences.delete.deleted', audience.name),
      timeout: 3000
    })
  }

  const associateSegmentToCustomer = async (customerId: number, segmentIds) => {
    const uniqueSegmentIds = [...new Set(segmentIds)]
    try {
      await axios.post(`customers/${customerId}/segments`, { segmentIds: uniqueSegmentIds })
    } catch (e) {
      notification.create('error', {
        text: t('notifications.save.error'),
        timeout: 5000
      })
      if (e.response.status === 429) {
        throw new Error('Rate limit error')
      }
      throw new Error(e)
    }
  }

  const segmentTypes: {
    id: string
    color: ChipColor
    icon: Icons
    text: string
    description?: string
    disabled: boolean
  }[] = [
    {
      id: 'static',
      color: 'orange',
      icon: 'HeroSquare3Stack3DIconSolid',
      text: t('pages.customer.audiences.table.segments.static'),
      description: t('pages.customer.audiences.create.segments.static.description'),
      disabled: false
    },
    {
      id: 'dynamic',
      color: 'indigo',
      description: t('pages.customer.audiences.create.segments.dynamic.description'),
      icon: 'HeroBoltIconSolid',
      text: t('pages.customer.audiences.table.segments.dynamic'),
      disabled: false
    },
    {
      id: 'typology',
      color: 'violet',
      icon: 'HeroBoltIconSolid',
      text: t('pages.customer.audiences.table.segments.typology'),
      disabled: false
    }
  ]

  const formatSegmentsByTypes = (
    customerLists: {
      id: number
      name: string
      type: string
      frequency_type: string
    }[]
  ) => {
    if (!customerLists.length) return []

    return [
      {
        type: 'static',
        text: t('pages.customer.audiences.table.segments.static'),
        values: customerLists.filter((item) => item.type === 'static' && !item.frequency_type)
      },
      {
        type: 'dynamic',
        text: t('pages.customer.audiences.table.segments.dynamic'),
        values: customerLists.filter((item) => item.type === 'dynamic' && !item.frequency_type)
      },
      {
        type: 'typology',
        text: t('pages.customer.audiences.table.segments.typology'),
        values: customerLists.filter((item) => item.type === 'dynamic' && item.frequency_type)
      }
    ]
  }

  return {
    editAudienceScope,
    associateSegmentToCustomer,
    basicDateOperators,
    bulkUpdateAudienceRules,
    createAudience,
    deleteAudience,
    determineRuleType,
    editAudienceName,
    editAudienceStores,
    fetchAudienceById,
    fetchAudienceByTemplate,
    fetchAudienceRules,
    fetchEstimateAudience,
    fetchMerchantAudienceMass,
    fetchMerchantAudienceCustomersCount,
    fetchMerchantAudiences,
    formatAudienceRulesRecordToArray,
    formatSegmentsByTypes,
    handleSubmitSegmentRules,
    isAudienceRuleWithParameters,
    isRuleValueValid,
    relativeOperators,
    segmentTypes
  }
}
